<script setup>
import { computed, inject, ref } from 'vue'
import { useApi } from '@provise/composables/api'
import { useMessageStack } from '@provise/stores/messagestack'
import { useToggle } from '@vueuse/core'
import Stickypanel from '@provise/components/stickypanel.vue'
import Step from '../step.vue'
import ReplyItem from './replyItem.vue'
import CreateReply from './create.vue'
import { REPLY_STATES } from '../../item.vue'
import ReplyUpload from '@/views/complaints/item/reply_upload.vue'

defineProps({
  edit: Boolean,
  editPermission: {
    type: Boolean,
    default: true,
  },
})
defineEmits(['save'])
const model = defineModel()
const doc = ref({})
const docTemplates = ref([])

const { push: notify } = useMessageStack()
const [showCreate, toggleCreate] = useToggle(false)
const [replies, listReplies] = inject('replies')
useApi({ url: 'doc_templates', model: docTemplates, immediate: true, pick: d => d.data || [] })

const { refresh: createReply } = useApi({
  url: `complaints/${model.value.id}/reply`,
  method: 'post',
  model: doc,
})

const currUser = computed(() => {
  return model.value.sla_users?.prepare_to_answer || null
})

function onReplyUploaded() {
  notify('Ответ создан')
  toggleCreate(false)
  listReplies()
}

async function onSave() {
  toggleCreate(false)
  doc.value = {}
  await listReplies()
}

function closeModal() {
  doc.value = {}
  toggleCreate()
}
</script>

<template>
  <Step :edit="edit">
    <template #plain v-if="replies.length">
      <dl class="grid mb-0" style="row-gap: 0">
        <template v-for="reply of replies" :key="reply.id">
          <dt class="g-col-3">Файл:</dt>
          <dd class="g-col-9 mb-0 hstack gap-2">
            <a download :href="reply.link" v-if="reply.link">{{ reply.display_name }}</a>
            <span v-else>{{ reply.display_name }}</span>
            <span
              class="text-success fs-6 fw-bold"
              v-if="reply.get_state === REPLY_STATES.STATE_DONE"
              >Согласован</span
            >
            <span
              class="text-warning fs-6 fw-bold"
              v-if="reply.get_state === REPLY_STATES.STATE_WAIT"
              >Ожидает согласования</span
            >
            <span
              class="text-warning fs-6 fw-bold"
              v-if="reply.get_state === REPLY_STATES.STATE_WAIT_COMMERCE"
              >Ожидает согласования коммерческим отделом</span
            >
            <span
              class="text-danger fs-6 fw-bold"
              v-if="reply.get_state === REPLY_STATES.STATE_DENY"
              >На доработке</span
            >
          </dd>
          <template v-if="reply.comment">
            <dt class="g-col-3">Комментарий:</dt>
            <dd class="g-col-9">{{ reply.comment }}</dd>
          </template>
          <template v-if="reply.approval_comment_label">
            <dt class="g-col-3">Замечание:</dt>
            <dd class="g-col-9" v-html="reply.approval_comment_label" />
          </template>
        </template>
      </dl>
    </template>
    <div class="vstack gap-3">
      <template v-if="replies.length">
        <h6>Ответы</h6>
        <ReplyItem
          v-for="reply of replies"
          :key="reply.id"
          :model-value="reply"
          @deleted="listReplies"
          @edited="listReplies"
          :disabled="!editPermission"
          :disable-checkbox="model.status.key === 'prepare_to_answer'"
        />
        <slot name="sla" />
      </template>

      <div class="card" v-if="showCreate && editPermission">
        <div class="card-header">
          <div class="d-flex">
            Новый документ
            <button type="button" class="btn-close ms-auto" @click="closeModal()" />
          </div>
        </div>
        <div class="card-body">
          <CreateReply :api-fn="createReply" v-model="doc" @save="onSave" />
        </div>
      </div>

      <div class="hstack gap-2 flex-wrap" v-if="!currUser && editPermission">
        <button type="button" class="btn btn-outline-primary" @click="$emit('save')">
          <x-icon icon="pencil" />
          Взять в работу
        </button>
      </div>
      <div class="hstack gap-2 flex-wrap" v-else-if="!showCreate && editPermission">
        <Stickypanel>
          <div class="me-2">
            <ReplyUpload v-model="model" @done="onReplyUploaded" />
          </div>
          <button
            type="button"
            class="btn btn-outline-blue"
            @click="toggleCreate()"
            v-if="!showCreate"
          >
            <i class="bi bi-file-earmark-plus" />
            Создать новый документ
          </button>
          <slot name="form-post-controls" />
        </Stickypanel>
      </div>
      <div v-if="!editPermission" class="alert alert-info">
        Редактирование невозможно. У вас нет прав
      </div>
    </div>
  </Step>
</template>
